import { AttachmentModel, DevicesModel } from "./Common";

export interface UrlsOptions {
  blacklisted_urls?: [];
  whitelisted_urls?: [];
}
export interface ActiveTimingOptions {
  days?: {
    monday: boolean,
      tuesday: boolean,
      wednesday: boolean,
      thursday: boolean,
      friday: boolean,
      saturday: boolean,
      sunday: boolean,
  };
  time?: {
    from?: string;
    to?: string;
    timezone?: string;
  };
}
export interface BannedUsersOptions {
  banned_users?: [];
}
export interface CannedResponsesOptions {
  canned_responses?: [];
}
export interface MessageToneOptions {
  enabled?: boolean;
  volume_level?: number;
  sound_file?: File;
  Visibility?: DevicesModel;
}
export interface ToolTipOptions {
  enabled?: boolean;
  tooltip_text?: string;
  devices?: DevicesModel;
  position?: string;
}

export interface EmailNotificationOptions {
  enabled?: boolean;
  emails?: [];
  email_view?: string;
}
export interface SmsNotificationOptions {
  enabled?: boolean;
  phone?: string;
}
export type WidgetSize = "compact" | "standard" | "tall" | "wide";

export const WIDGET_SIZE_PRESETS: Record<WidgetSize, { width: number; height: number }> = {
  compact: { width: 372, height: 560 },
  standard: { width: 404, height: 672 },
  tall: { width: 404, height: 780 },
  wide: { width: 468, height: 700 },
};

/**
 * What the widget asks the host for on top of the panel itself.
 *
 * getChatbotWidth takes it back off before the panel is drawn, and the iframe
 * we post is frameWidth - 16 too, so the panel lands exactly on the preset.
 */
export const WIDGET_FRAME_GUTTER = 16;

/**
 * Capped at 750, not 1200. getChatbotWidth returns a hard-coded 448 once the
 * frame reaches 768, so anything from 752 up rendered at 448 — the bound has
 * to stay inside what the renderer can actually honour.
 *
 * Only enforced on the rollout path (Home.tsx gates resolveCustomSize on
 * isNewUi) — deliberately, since clamping unconditionally would change what
 * a legacy bot with a stored custom_width in ~736-1200 renders at, and this
 * branch must not touch legacy rendering.
 */
export const CUSTOM_WIDTH_BOUNDS = { min: 280, max: 750 };
export const CUSTOM_HEIGHT_BOUNDS = { min: 400, max: 1000 };

export const clampCustomSize = (
  value: unknown,
  bounds: { min: number; max: number }
): number | null => {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return null;
  return Math.min(Math.max(size, bounds.min), bounds.max);
};

export type Surface = "docked" | "expanded";

// vw/vh here are the widget iframe's, not the host page's — the host sizes the
// iframe from the CHATBOT_RESIZE we post. Using the full 100 means the panel
// fills whatever it is granted instead of leaving a margin inside an iframe
// that was already sized for it.
export const SURFACE_PANEL_WIDTH: Record<"expanded", string> = {
  expanded: "min(1200px, 100vw)",
};

export const SURFACE_PANEL_HEIGHT: Record<"expanded", string> = {
  expanded: "min(920px, 100vh)",
};

export interface ChatbotConfiguration {
  desktop_triggering_time?: number;
  mobile_triggering_time?: number;
  mobile_auto_triggering?: boolean;
  desktop_auto_triggering?: boolean;
  urls?: UrlsOptions;
  before_exit_triggering?: boolean;
  consent_configuration?: any;
  consent_triggering?: boolean;
  active_timings?: ActiveTimingOptions;
  banned_users?: BannedUsersOptions;
  canned_responses?: CannedResponsesOptions;
  enabled_devices?: DevicesModel;
  message_tone?: MessageToneOptions;
  otp_validation?: boolean;
  email_otp_validation?: boolean;
  whatsapp_otp_validation?: boolean;
  global_user_input_enabled?: boolean;
  remove_branding?: boolean;
  response_delay?: boolean;
  tooltip?: ToolTipOptions;
  avatar?: AttachmentModel;
  background_image?: AttachmentModel;
  company_logo?: AttachmentModel;
  email_notification?: EmailNotificationOptions;
  sms_notification?: SmsNotificationOptions;
  whatsapp_notification?: any;
  sheet_notification?: any;
  display_position?: string;
  default_messages?: DefaultMessages;
  show_suggestion?: boolean;
  notification_block?:NotificationBlock;
  quick_menu?:QuickMenu;
  banner_carousel?:BannerCarousel;
  default_country?:string;
  widget_size?: WidgetSize;
  custom_width?: number;
  custom_height?: number;
}


interface NotificationBlock {
  enabled?: boolean;
  selected_icon?:number;
  data?:any;
}
interface QuickMenu {
  view_style?:number | string;
  enabled?: boolean;
  data?:any;
}
interface BannerCarousel {
  enabled?: boolean;
  data?:any;
}

export interface ChatbotLayout {
  display_position: string;
}

export interface DefaultMessages {
  conflict: {
    name: string,
    response: any[],
    responseId: string,
    type: string,
  },
  default: {
    name: string,
    response: any[],
    responseId: string,
    type: string,
  },
  live_agent: {
    retry: number,
    enabled: boolean,
  },
  outside_business_hours: {
    response: any[],
    user_info: {
      name: boolean,
      email: boolean,
      phone: boolean,
    },
  },
}

/**
 * The docked frame width, from the preset rather than from the device-size
 * store.
 *
 * The store is only written when the host sends a resize, and that fires
 * before the bot config has loaded — so it records the fallback size. Height
 * was always computed from the preset directly, width was read back out of the
 * store, and the two disagreed: picking Tall or Wide moved the height and left
 * the width where it started.
 *
 * Home and MainUI both need this, which is how they drifted apart in the first
 * place.
 */
export const resolveDockedFrameWidth = ({
  preset,
  customWidth,
  storeWidth,
}: {
  preset: { width: number } | null;
  customWidth: number | null;
  storeWidth: number;
}): number => {
  if (customWidth) return customWidth + WIDGET_FRAME_GUTTER;
  if (preset) return preset.width + WIDGET_FRAME_GUTTER;
  return storeWidth;
};
